'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useCalendarStore } from '@/stores/calendar-store';
import { LAYOUT_OPTIONS } from '@/lib/brand';
import { uiFb } from '@/lib/i18n';

interface ViewSwitcherProps {
  /** When true, render a single dropdown trigger instead of inline segmented control */
  compact?: boolean;
  /** When true, render dropdown on mobile, inline segmented control on md:+ */
  responsive?: boolean;
}

export function ViewSwitcher({ compact, responsive }: ViewSwitcherProps) {
  const { calendarLayout, setCalendarLayout, lang } = useCalendarStore();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Arrow key navigation within dropdown
  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    if (!items || items.length === 0) return;
    const currentIndex = Array.from(items).findIndex(el => el === document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      items[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      items[prev].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  }, []);

  const activeOption = LAYOUT_OPTIONS.find(s => s.id === calendarLayout) || LAYOUT_OPTIONS[0];
  const activeLabel = uiFb(`ui.view_${activeOption.id}`, activeOption.label, lang);
  const layoutLabel = uiFb('ui.layout', 'Layout', lang);

  /* ── Compact dropdown renderer ── */
  const renderDropdown = (wrapperClassName?: string) => (
    <div className={`relative ${wrapperClassName || ''}`} ref={menuRef}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
        aria-label={uiFb('ui.switch_view', 'Switch view', lang)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>{activeOption.icon}</span>
        <span className="text-text-tertiary">{layoutLabel}:</span> <span>{activeLabel}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
          <path d="M2.5 4 L5 6.5 L7.5 4" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[140px] content-card p-1 shadow-lg animate-in fade-in slide-in-from-top-1 duration-150"
          onKeyDown={handleDropdownKeyDown}
        >
          {LAYOUT_OPTIONS.map((option) => (
            <button
              key={option.id}
              role="menuitem"
              aria-selected={calendarLayout === option.id}
              onClick={() => {
                setCalendarLayout(option.id);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${
                calendarLayout === option.id
                  ? 'font-semibold text-text-primary'
                  : 'text-text-secondary'
              }`}
            >
              <span className="text-sm">{option.icon}</span>
              {uiFb(`ui.view_${option.id}`, option.label, lang)}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  /* ── Inline segmented control renderer ── */
  const renderSegmented = (wrapperClassName?: string) => (
    <div className={`flex items-center gap-1.5 ${wrapperClassName || ''}`}>
      <span className="text-xs text-text-tertiary font-medium">{layoutLabel}</span>
      <div className="flex items-center gap-0.5 rounded-md bg-surface-elevated p-0.5">
        {LAYOUT_OPTIONS.map((option) => (
          <button
            key={option.id}
            onClick={() => setCalendarLayout(option.id)}
            aria-label={uiFb(`ui.switch_view`, `Switch to ${option.label} view`, lang, { label: option.label })}
            aria-pressed={calendarLayout === option.id}
            className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-xs font-medium transition-all ${
              calendarLayout === option.id
                ? 'bg-brand text-text-on-brand shadow-sm'
                : 'text-muted hover:bg-surface-hover hover:text-text-secondary'
            }`}
          >
            <span className="text-sm">{option.icon}</span>
            {uiFb(`ui.view_${option.id}`, option.label, lang)}
          </button>
        ))}
      </div>
    </div>
  );

  if (compact) {
    return renderDropdown();
  }

  if (responsive) {
    return (
      <>
        {renderDropdown('md:hidden')}
        {renderSegmented('hidden md:flex')}
      </>
    );
  }

  return renderSegmented();
}
